import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { apiClient as defaultClient, type ApiClient } from '../api/client';
import { isUnauthorizedError } from '../api/errors';
import { AuthContext, type AuthContextValue, type AuthStatus } from './context';

export type { AuthContextValue, AuthStatus } from './context';

export interface AuthProviderProps {
  children: ReactNode;
  /** Injectable client (tests) and initial status (tests/guards). */
  client?: ApiClient;
  initialStatus?: AuthStatus;
  /** Seeds the signed-in user id (tests). */
  initialUserId?: string | null;
}

/**
 * Infers session state from API responses — the session cookie is HttpOnly, so
 * JS cannot read auth state directly. On mount (when `status === 'probing'`) it
 * probes GET /auth/me exactly once to rehydrate the session across a refresh or
 * deep-link: a 200 => authenticated; a 401, network error, or any other failure
 * => anonymous (fail-safe — protected content is never shown without a confirmed
 * 200). Login success => authenticated; logout or any UnauthorizedError =>
 * anonymous.
 */
export function AuthProvider({
  children,
  client = defaultClient,
  initialStatus = 'probing',
  initialUserId = null,
}: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(initialStatus);
  const [userId, setUserId] = useState<string | null>(initialUserId);

  // Rehydrate the session once on mount. `probed` guarantees a single /auth/me
  // even under React 18 StrictMode's double-invoke; `mounted` (re-set by the
  // paired invoke) lets the resolve apply while still short-circuiting after a
  // real unmount.
  const probed = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    if (initialStatus === 'probing' && !probed.current) {
      probed.current = true;
      void client
        .me()
        .then((me) => {
          if (!mounted.current) return;
          setUserId(me.userId);
          setStatus('authenticated');
        })
        .catch(() => {
          // 401, 5xx, or network failure: fail safe to anonymous. Never wedge in
          // the loading state and never render protected content without a 200.
          if (!mounted.current) return;
          setUserId(null);
          setStatus('anonymous');
        });
    }
    return () => {
      mounted.current = false;
    };
  }, [client, initialStatus]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const result = await client.login(email, password);
        setUserId(result.userId);
        setStatus('authenticated');
      } catch (err) {
        if (isUnauthorizedError(err)) {
          setStatus('anonymous');
          setUserId(null);
        }
        throw err;
      }
    },
    [client],
  );

  const register = useCallback(
    async (email: string, password: string) => {
      // Register returns no session; auto-login immediately so the user lands
      // authenticated. A failure at either step surfaces to the caller.
      await client.register(email, password);
      const result = await client.login(email, password);
      setUserId(result.userId);
      setStatus('authenticated');
    },
    [client],
  );

  const logout = useCallback(async () => {
    try {
      await client.logout();
    } catch {
      // Logout is best-effort: a failed server call still signs the client out.
    } finally {
      setStatus('anonymous');
      setUserId(null);
    }
  }, [client]);

  const handleUnauthorized = useCallback(() => {
    setStatus('anonymous');
    setUserId(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, userId, login, register, logout, handleUnauthorized }),
    [status, userId, login, register, logout, handleUnauthorized],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
